import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { fetchChildSavingsInterestSettings, updateChildSavingsInterestSettings, fetchChildAccounts } from '../../../api';
import MyButton from '../../basic/MyButton';
import MyToggle from '../../basic/MyToggle';
import MyRadioGroup from '../../basic/MyRadioGroup';
import MyInterestRatesSlider from '../../basic/MyInterestRatesSlider';
import MySpinner from '../../basic/MySpinner';
import { HelpCircle } from 'lucide-react';

import './ModalSavingsInterestRates.css';

Modal.setAppElement('#root');

const tooltip_preview = `<p><strong>Preview</strong> projections do not take into account allowances or other manual deposits.</p>`;

const Preview = ({ accounts, interestRate, compoundFrequency, ratePeriod }) => {
    const calculateGrowth = (balance, days) => {
        // Convert any rate to an annual rate
        const annualRate = (interestRate / 100) * 
          (ratePeriod === 'weekly' ? 52 : 
           ratePeriod === 'monthly' ? 12 : 1);
        
        // Determine compounding periods per year
        const periodsPerYear = compoundFrequency === 'daily' ? 365 : 52;
        
        // Calculate rate per compounding period
        const ratePerPeriod = annualRate / periodsPerYear;
        
        // Calculate number of compounding periods for the given days
        const periods = compoundFrequency === 'daily' ? days : Math.floor(days / 7);
      
        // Apply compound interest formula
        return balance * Math.pow(1 + ratePerPeriod, periods);
      };

  const formatCurrency = (amount) => {
    return `$${Math.ceil(amount * 100) / 100}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const calculatePercentageGrowth = (initial, final) => {
    return ((final - initial) / initial * 100).toFixed(2);
  };

  return (
    <div className="sir-preview">
      <h4>
        <span className="label-with-icon">
            Preview (applies to all savings accounts):
            <span className="tooltip-container">
                <HelpCircle size={18} className="tooltip-icon" color="#4CAF50" />
                <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltip_preview }}></span>
            </span>
        </span>
      </h4>
      {accounts.map((account, index) => {
        const oneWeekGrowth = calculateGrowth(account.account_balance, 7);
        const oneMonthGrowth = calculateGrowth(account.account_balance, 30);
        const oneYearGrowth = calculateGrowth(account.account_balance, 365);
        const monthlyGrowthAmount = oneMonthGrowth - account.account_balance;
        const monthlyGrowthPercentage = calculatePercentageGrowth(account.account_balance, oneMonthGrowth);

        return (
          <div key={index} className="sir-preview-account">
            <p className="sir-preview-account-growth">
              {account.account_name} growth: {formatCurrency(monthlyGrowthAmount)} in 30 days ({monthlyGrowthPercentage}%)
            </p>
            <table className="sir-preview-growth-table">
              <thead>
                <tr>
                  <th>Current</th>
                  <th>1 week</th>
                  <th>1 month</th>
                  <th>1 year</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{formatCurrency(account.account_balance)}</td>
                  <td>{formatCurrency(oneWeekGrowth)}</td>
                  <td>{formatCurrency(oneMonthGrowth)}</td>
                  <td>{formatCurrency(oneYearGrowth)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
      {accounts.length === 0 && <p>No savings accounts found.</p>}
    </div>
  );
};

const ModalSavingsInterestRates = ({ isOpen, onClose, childId, onSuccess }) => {
  const [settings, setSettings] = useState({
    is_enabled: false,
    interest_rate: 0,
    compound_frequency: 'daily',
    rate_period: 'weekly',
    max_balance: 0
  });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFormChanged, setIsFormChanged] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchChildSavingsInterestSettings(childId);
      setSettings(data);
      const accountsData = await fetchChildAccounts(childId);
      setAccounts(accountsData.filter(account => account.is_savings));
    } catch (err) {
      console.error("Error fetching data:", err);
      setError('Failed to load settings or accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (isOpen) {
      setIsFormChanged(false);
      fetchSettings();
    }
  }, [isOpen, fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setIsFormChanged(true);
  };

  const handleToggleChange = (event) => {
    const newIsEnabled = event.target.checked;
    setSettings(prev => ({
      ...prev,
      is_enabled: newIsEnabled
    }));
    setIsFormChanged(true);
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
    setIsFormChanged(true);
  };

  const handleSliderChange = (newValue) => {
    setSettings(prev => ({
      ...prev,
      interest_rate: newValue
    }));
    setIsFormChanged(true);
  };

  const ratePeriodOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'annual', label: 'Annual' }
  ];

  const compoundFrequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' }
  ];

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      await updateChildSavingsInterestSettings(childId, settings);
      onSuccess('Savings interest settings updated successfully.');
      onClose();
    } catch (err) {
      setError('Failed to update savings interest settings. Please try again.');
      console.error("Error in update of savings interest settings: ", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;


  const tooltip_title = `<p>For more information visit this article in our community forum <a href="https://community.piggybunker.com/questions/10010000000000009/introduction-to-interest-rates-a-parent-s-guide" target="_blank"><strong>Introduction to Interest Rates</strong></a>.</p>`;
 
  const tooltip_interstrateperiod = 
    `<p><strong>Interest Rate Period</strong>: This sets how often interest is calculated. </p>
     <p>For example, 5% weekly would grow $100 to $112.99 in a month, while 5% yearly would only grow it to $100.41 in the same time.</p>`;

    const tooltip_compondfreq = `
    <p><strong>Compound Frequency</strong>: This determines how often interest is deposited to Savings accounts. </p>
    <p>More frequent compounding (like daily) can help savings grow faster and be more engaging for children than less frequent (like weekly).</p>`;



    const tooltip_maxbalance = `
        <p><strong>Interest Cap</strong>: This is the maximum total balance across all Savings and Goal accounts that will earn interest. beyond this amount no interest deposits will be added to Savings account.</p>
        <p>Which means that its time to change the interest rates period or percentge to moderate the growth</p>`;



  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Edit Savings Interest Rates"
      className="sir-modal"
      overlayClassName="sir-overlay"
    >
      <div className="sir-content">
        <h2 className="sir-title">Savings Accounts Interest Rates</h2>
        <p className="sir-description">
            Adjust interest rates for all Savings accounts.
            <span className="tooltip-container">
                <HelpCircle size={16} className="tooltip-icon" color="#4CAF50" />
                <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltip_title }}></span>
            </span>
        </p>
        {loading ? (
          <div className="sir-loading">
            <MySpinner size="large" color="#007AFF" />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            <div className="sir-form">
              <div className="sir-form__toggle">
                <MyToggle
                  label="Enable Savings Interest"
                  checked={settings.is_enabled}
                  onChange={handleToggleChange}
                />
              </div>
              <label className={`sir-form__label ${!settings.is_enabled ? 'disabled' : ''}`}>
                <span className="label-with-icon">
                    Interest Rate Period:
                    <span className="tooltip-container">
                        <HelpCircle size={18} className="tooltip-icon" color="#4CAF50" />
                        <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltip_interstrateperiod }}></span>
                    </span>
                </span>
                <div onClick={(e) => e.stopPropagation()} style={{ isolation: 'isolate' }}>
                  <MyRadioGroup
                    name="rate_period"
                    options={ratePeriodOptions}
                    value={settings.rate_period}
                    onChange={handleRadioChange}
                    disabled={!settings.is_enabled}
                  />
                </div>
                </label>
              <label className={`sir-form__label ${!settings.is_enabled ? 'disabled' : ''}`}>
                Interest Rate (%):
                <MyInterestRatesSlider
                  value={settings.interest_rate}
                  onChange={handleSliderChange}
                  disabled={!settings.is_enabled}
                />
              </label>
              <label className={`sir-form__label ${!settings.is_enabled ? 'disabled' : ''}`}>
                <span className="label-with-icon">
                    Compound Frequency:
                    <span className="tooltip-container">
                        <HelpCircle size={18} className="tooltip-icon" color="#4CAF50" />
                        <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltip_compondfreq }}></span>
                    </span>
                </span>
                <MyRadioGroup
                  name="compound_frequency"
                  options={compoundFrequencyOptions}
                  value={settings.compound_frequency}
                  onChange={handleRadioChange}
                  disabled={!settings.is_enabled}
                />
              </label>
            </div>
            <hr className="sir-separator" />
            <Preview
              accounts={accounts}
              interestRate={settings.interest_rate}
              compoundFrequency={settings.compound_frequency}
              ratePeriod={settings.rate_period}
            />
            <hr className="sir-separator" />
            <label className={`sir-form__label ${!settings.is_enabled ? 'disabled' : ''}`}>
                <span className="label-with-icon">
                    Interest-Earning Balance Cap:
                    <span className="tooltip-container">
                        <HelpCircle size={16} className="tooltip-icon" color="#4CAF50" />
                        <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltip_maxbalance }}></span>
                    </span>
                </span>
              <input
                type="number"
                name="max_balance"
                value={settings.max_balance}
                onChange={handleInputChange}
                min="0"
                disabled={!settings.is_enabled}
              />
            </label>
            <div className="sir-actions">
              <MyButton
                text="Save Changes"
                onClick={handleSubmit}
                disabled={loading || !isFormChanged}
                color="white"
                hoverColor="white"
              />
              <MyButton
                text="Cancel"
                onClick={onClose}
                backgroundColor="white"
                color="#dc3545"
                hoverBackgroundColor="#f8f8f8"
                hoverColor="#c82333"
              />
            </div>
            {error && <div className="sir-error-message">{error}</div>}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ModalSavingsInterestRates;